<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { riskConfig } from '../riskConfig'
import { useI18n } from 'vue-i18n'

const _arrow = 16
const _smallCircle = 10
const _colorCircle = 32
const font = 48

const { t } = useI18n()

const props = defineProps<{
	value?: number
	type: 'horizontal' | 'vertical' | 'impact' | 'probability'
	color?: string
}>()

const canvasRef = ref<HTMLCanvasElement | null>(null)
const style = ref('')

const obs = new MutationObserver((obs) => {
	const target = obs[0].target as HTMLElement
	if (target.classList.contains('show')) draw()
})

function getCanvasColor(ctx: CanvasRenderingContext2D) {
	return getComputedStyle(ctx.canvas).color
}

watch(canvasRef, (canvas) => {
	draw()

	const tab = canvas?.closest('.tab-pane')
	if (!tab) return
	obs.observe(tab, { attributes: true })
})
watch(riskConfig, draw, { deep: true })
onMounted(() => window.addEventListener('resize', draw))
onUnmounted(() => window.removeEventListener('resize', draw))

function draw() {
	window.requestAnimationFrame(_draw)
}

function _draw() {
	if (!canvasRef.value) return

	const canvas = canvasRef.value
	if (!canvas || !canvas.parentElement) return

	const rect = canvas.parentElement.getBoundingClientRect()

	canvas.width = rect.width * 2
	canvas.height = rect.height * 2

	style.value = `width: ${rect.width}px; height: ${rect.height}px;`

	const middleX = canvas.width / 2
	const middleY = canvas.height / 2

	const safeX = canvas.width / 3
	const safeY = canvas.height / 3

	const padding = Math.sqrt(safeX * safeY)
	const resoMult = padding / 120

	const arrow = _arrow * resoMult
	const smallCircle = _smallCircle * resoMult
	const colorCircle = _colorCircle * resoMult

	const ctx = canvas.getContext('2d')!
	ctx.font = font * resoMult + 'px sans-serif'
	ctx.clearRect(0, 0, canvas.width, canvas.height)

	switch (props.type) {
		case 'vertical':
			line(ctx, padding, 0, padding, canvas.height)
			line(ctx, padding, middleY, padding + safeX, middleY)
			circle(ctx, padding + safeX, middleY, colorCircle, props.color)

			if (props.value !== undefined) text(ctx, padding + safeX, middleY, props.value.toString(), 'white', true, 'center')

			if (props.value === riskConfig.value.matrixSize) {
				line(ctx, padding, 0, padding - arrow, arrow)
				line(ctx, padding, 0, padding + arrow, arrow)
			}
			break

		case 'horizontal':
			line(ctx, 0, canvas.height - padding, canvas.width, canvas.height - padding)
			line(ctx, middleX, canvas.height - padding, middleX, canvas.height - padding - safeY)
			circle(ctx, middleX, canvas.height - padding - safeY, colorCircle, props.color)

			if (props.value !== undefined) text(ctx, middleX, canvas.height - padding - safeY, props.value.toString(), 'white', true, 'center')

			if (props.value === riskConfig.value.matrixSize) {
				line(ctx, canvas.width, canvas.height - padding, canvas.width - arrow, canvas.height - padding - arrow)
				line(ctx, canvas.width, canvas.height - padding, canvas.width - arrow, canvas.height - padding + arrow)
			}
			break

		case 'impact':
			line(ctx, padding, 0, padding, safeY)
			line(ctx, padding, safeY, padding + safeX, safeY)
			circle(ctx, padding + safeX, safeY, smallCircle)
			text(ctx, padding + safeX + 2 * smallCircle, safeY, t('risk.impact'))
			break

		case 'probability':
			line(ctx, canvas.width - padding - smallCircle, canvas.height - padding, canvas.width, canvas.height - padding)
			circle(ctx, canvas.width - padding - smallCircle, canvas.height - padding, smallCircle)
			text(ctx, canvas.width - padding - 2 * smallCircle, canvas.height - padding, t('risk.probability') + ' ', undefined, false, 'right')
			break
	}
}

function line(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
	ctx.save()
	ctx.strokeStyle = getCanvasColor(ctx)
	ctx.beginPath()
	ctx.moveTo(x1, y1)
	ctx.lineTo(x2, y2)
	ctx.stroke()
	ctx.restore()
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color?: string) {
	ctx.save()
	ctx.beginPath()
	ctx.arc(x, y, r, 0, 2 * Math.PI)
	ctx.fillStyle = color || getCanvasColor(ctx)
	ctx.fill()
	ctx.restore()
}

function text(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	txt: string,
	color?: string,
	shadow?: boolean,
	align?: 'left' | 'right' | 'center',
) {
	ctx.save()

	const measure = ctx.measureText(txt)
	let xo = 0
	const yo = measure.actualBoundingBoxAscent / 2

	if (align === 'right') xo = measure.width
	else if (align === 'center') xo = measure.width / 2

	const finalColor = color || getCanvasColor(ctx)

	if (shadow) {
		ctx.shadowColor = 'black'
		ctx.shadowBlur = 2
		ctx.shadowOffsetX = 2
		ctx.shadowOffsetY = 2
	}

	ctx.fillStyle = finalColor
	ctx.fillText(txt, x - xo, y + yo)

	ctx.restore()
}
</script>

<template>
	<canvas ref="canvasRef" class="flex-fill" :style="style" />
</template>

<style lang="sass" scoped>
canvas
  color: var(--bs-body-color)
  border-radius: 1em
</style>
